<template>
  <div>
    <site-header />
    <main v-if="loading" class="empty-state">
      <h1>Loading product...</h1>
    </main>
    <template v-else-if="product">
      <main class="detail-page">
        <section class="detail-gallery">
          <div class="detail-gallery-scroll">
            <button
              v-for="image in product.gallery"
              :key="image"
              type="button"
              :class="{ active: selectedImage === image }"
              @click="selectedImage = image"
            >
              <img :src="image" :alt="product.name">
            </button>
          </div>
          <div class="detail-main-image">
            <img :src="selectedImage || product.image" :alt="product.name">
          </div>
        </section>
        <section class="detail-info">
          <router-link :to="{ path: '/items', query: { category: product.category } }">{{ product.category }}</router-link>
          <h1>{{ product.name }}</h1>
          <div class="price-row big">
            <strong>${{ product.price.toFixed(2) }}</strong>
            <del v-if="product.oldPrice">${{ product.oldPrice.toFixed(2) }}</del>
          </div>
          <p class="detail-copy">{{ product.description }}</p>

          <div v-for="attribute in visibleAttributes" :key="attribute.name" class="option-picker">
            <span>{{ attribute.name }}</span>
            <button
              v-for="value in attribute.values"
              :key="`${attribute.name}-${value}`"
              :class="{ active: selectedOptions[attribute.name] === value }"
              @click="selectOption(attribute.name, value)"
            >
              {{ value }}
            </button>
          </div>

          <button class="add-bag" @click="addProduct">Add to Bag</button>
          <ul class="service-list">
            <li>Free shipping over $50</li>
            <li>30-day returns</li>
            <li>Secure checkout</li>
          </ul>
        </section>
      </main>
      <section v-if="recommendationLoading || recommendedProducts.length" class="section-block detail-recommendations">
        <div class="section-title">
          <p>Recommended</p>
          <h2>You may also like</h2>
          <router-link :to="{ path: '/items', query: { category: product.category } }">View category</router-link>
        </div>
        <div v-if="recommendationLoading" class="loading-state">Loading recommendations...</div>
        <div v-else class="product-grid">
          <product-card v-for="item in recommendedProducts" :key="item.id" :product="item" />
        </div>
      </section>
    </template>
    <main class="empty-state" v-else>
      <h1>Product not found</h1>
      <router-link to="/items">Back to shop</router-link>
    </main>
    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import ProductCard from '@/components/ProductCard.vue'
import { addToCart } from '@/utils/cart'
import { queryGoods, queryGoodsDetail } from '@/api/goods'

export default {
  name: 'ProductDetail',
  components: { SiteHeader, SiteFooter, ProductCard },
  data() {
    return {
      product: null,
      selectedImage: '',
      selectedOptions: {},
      recommendedProducts: [],
      loading: false,
      recommendationLoading: false
    }
  },
  created() {
    this.loadProduct()
  },
  watch: {
    '$route.params.id'() {
      this.loadProduct()
    }
  },
  computed: {
    visibleAttributes() {
      if (this.product && this.product.attributes && this.product.attributes.length) {
        return this.product.attributes
      }

      return this.product ? [{ name: 'Size', values: this.product.sizes }] : []
    }
  },
  methods: {
    async loadProduct() {
      this.loading = true
      try {
        this.product = await queryGoodsDetail(this.$route.params.id)
        this.prepareProductOptions()
        this.loadRecommendations()
      } finally {
        this.loading = false
      }
    },
    prepareProductOptions() {
      this.selectedOptions = {}
      this.selectedImage = this.product ? this.product.image : ''

      this.visibleAttributes.forEach((attribute) => {
        if (attribute.values && attribute.values.length) {
          this.$set(this.selectedOptions, attribute.name, attribute.values[0])
        }
      })
    },
    selectOption(name, value) {
      this.$set(this.selectedOptions, name, value)
    },
    async loadRecommendations() {
      if (!this.product) return
      this.recommendationLoading = true
      try {
        const res = await queryGoods({
          num: 1,
          size: 5,
          categoryType: this.product.categoryType || this.product.category
        })
        this.recommendedProducts = res.list.filter(item => item.id !== this.product.id).slice(0, 4)
      } finally {
        this.recommendationLoading = false
      }
    },
    addProduct() {
      addToCart(this.product, this.selectedOptions)
      this.$message.success('Added to bag')
    }
  }
}
</script>
